import React, { useCallback, useMemo } from 'react';
import {
  Box,
  CalendarDate,
  CalendarPlaceholderIcon,
  NavigationLeftIcon,
  RawButton,
  ShimmerBox,
  Text,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import Permissions from '@/common/permissions';
import { IEmployerCalendarEventDtoCategoryEnum } from '@/dtos';
import { useHasAccess } from '@/modules/core/hooks';
import theme from '@/styles/theme';

import { EmployerCalendarPayload } from '../EmployerCalendar.types';

import EmployerCalendarEventsItem from './EmployerCalendarEventsItem';

interface Props {
  isErrorPlaceholder?: boolean;
  children: React.ReactNode;
  isLoading?: boolean;
  events: EmployerCalendarPayload[];
  onResetSelectedDate?: () => void;
  selectedDate?: CalendarDate | null;
  currentMonth?: CalendarDate | null;
  isEmpty?: boolean;
}

const DATE_FORMAT = 'MMM DD';
interface Params {
  isPast?: boolean;
}

const ORDER_CATEGORIES: IEmployerCalendarEventDtoCategoryEnum[] = [
  IEmployerCalendarEventDtoCategoryEnum.FUNDING,
  IEmployerCalendarEventDtoCategoryEnum.PLAN_DATES,
];
const EmployerCalendarEvents: React.FC<Props> = ({
  isLoading,
  isErrorPlaceholder,
  children,
  events,
  selectedDate,
  onResetSelectedDate,
  currentMonth,
  isEmpty,
}) => {
  const sortCategories = (list: EmployerCalendarPayload[]) => {
    return _.sortBy(list, (item) => (item.category ? ORDER_CATEGORIES.indexOf(item.category) : -1));
  };

  const sortDate = (list: EmployerCalendarPayload[]) => {
    return _.sortBy(list, (item) => item.eventDate);
  };

  const futureEvents = useMemo(() => {
    let list =
      events.filter((item) => (item.eventDate ? item.eventDate.isAfter(dayjs().endOf('date')) : true)) || [];
    list = sortCategories(list);
    list = sortDate(list);
    return list;
  }, [events]);
  const todaysEvents = useMemo(() => {
    let list =
      events.filter((item) =>
        item.eventDate
          ? item.eventDate.isAfter(dayjs()) && item.eventDate.isBefore(dayjs().endOf('date'))
          : true
      ) || [];
    list = sortCategories(list);
    return list;
  }, [events]);

  const pastEvents = useMemo(() => {
    let list = events.filter((item) => (item.eventDate ? item.eventDate.isBefore(dayjs()) : true));
    list = sortCategories(list);
    list = sortDate(list).reverse();
    return list;
  }, [events]);

  const firstFutureDate = useMemo(() => {
    return futureEvents?.[0]?.eventDate?.format(DEFAULT_DATE_FORMAT) || '';
  }, [futureEvents]);

  const getIsDateFromNow = useCallback(
    (item: EmployerCalendarPayload) => {
      if (!selectedDate && item.eventDate) {
        return item.eventDate.format(DEFAULT_DATE_FORMAT) === firstFutureDate;
      }
      return false;
    },
    [firstFutureDate, selectedDate]
  );
  const isViewPermissions = useHasAccess([{ permission: Permissions.VIEW_INVOICES }]);
  const renderItem = useCallback(
    (events: EmployerCalendarPayload[], params?: Params) => {
      return events.map((item) => (
        <div key={item.id}>
          <Box margin={{ bottom: 'spacing12' }}>
            <EmployerCalendarEventsItem
              key={item.id}
              event={item}
              isDateFromNow={getIsDateFromNow(item)}
              isPast={params?.isPast}
              isViewPermissions={isViewPermissions}
            />
          </Box>
        </div>
      ));
    },
    [getIsDateFromNow, isViewPermissions]
  );
  const currentTitle = useMemo(() => {
    let len = (futureEvents?.length || 0) + (todaysEvents?.length || 0);
    if (!currentMonth?.isSame(dayjs(), 'month')) {
      len = events.length;
    }
    if (selectedDate) {
      return `${events.length} Events on ${selectedDate.format(DATE_FORMAT)}`;
    }
    return `${len} Events this month`;
  }, [currentMonth, events.length, futureEvents?.length, selectedDate, todaysEvents?.length]);
  return (
    <Box
      data-testid="employer-calendar-events"
      direction="column"
      background="canvas"
      round="container1Round"
      border={{ color: 'border2', size: '1px' }}
      pad={{
        top: 'spacing8',
        left: 'spacing16',
        right: 'spacing16',
        bottom: 'spacing12',
      }}
      style={{ flex: 1 }}
    >
      <Box direction="row" justify="between" height="40px" align="center">
        <Box direction="row" align="center">
          {selectedDate && (
            <RawButton data-testid="employer-calendar-events_back-button" onClick={onResetSelectedDate}>
              <Box direction="row" align="center" margin={{ right: 'spacing12' }}>
                <NavigationLeftIcon color="iconAccent" size="20px" />
                <Text color="textAccent" margin={{ left: 'spacing8' }}>
                  Back
                </Text>
              </Box>
            </RawButton>
          )}

          <Text data-testid="employer-calendar-events_title" size="medium" weight={400}>
            {currentTitle}
          </Text>
        </Box>
        <div>{children}</div>
      </Box>
      <Box direction="column" align="center" justify="center" style={{ flex: 1 }}>
        <Box
          data-testid="employer-calendar-events_list"
          direction="column"
          overflow="auto"
          width="100%"
          style={{ flex: 1 }}
          height={{ max: '370px' }}
          gap="spacing12"
        >
          {isLoading ? (
            <Box data-testid="employer-calendar-events_loading" direction="column">
              {_.times(3, () => (
                <Box
                  round="container2Round"
                  background="module"
                  margin={{ bottom: 'spacing12' }}
                  pad={{ vertical: 'spacing8', horizontal: 'spacing16' }}
                >
                  <ShimmerBox round="fieldRound" width={`${_.random(20, 60)}%`} height="18px" />
                  <ShimmerBox
                    round="fieldRound"
                    margin={{ top: 'spacing4' }}
                    width={`${_.random(20, 60)}%`}
                    height="18px"
                  />
                </Box>
              ))}
            </Box>
          ) : events.length ? (
            <>
              {renderItem(todaysEvents)}
              {renderItem(futureEvents)}
              {!!pastEvents.length && (
                <Text
                  data-testid="employer-calendar-events_past-label"
                  size="medium"
                  weight={400}
                  margin={{ bottom: 'spacing12' }}
                >
                  Past events
                </Text>
              )}
              {renderItem(pastEvents, {
                isPast: true,
              })}
            </>
          ) : (
            <Box
              data-testid="employer-calendar-events_empty"
              direction="column"
              align="center"
              justify="center"
              style={{ flex: 1 }}
            >
              <CalendarPlaceholderIcon size="72px" />
              <Text size="medium" weight={700}>
                {isErrorPlaceholder
                  ? 'Failed to load events'
                  : selectedDate
                    ? 'No events on this date'
                    : 'No events this month'}
              </Text>
              <Text size="medium" textAlign="center" weight={400}>
                {isErrorPlaceholder
                  ? 'Please check your connection and try again.'
                  : selectedDate
                    ? 'Select another date or view all events'
                    : 'Navigate to another month to see events'}
              </Text>
            </Box>
          )}
        </Box>
      </Box>
      {!isEmpty && !!events.length && (
        <Box data-testid="employer-calendar-events_legend" direction="row" margin={{ top: 'spacing12' }}>
          <Box direction="row" align="center" margin={{ right: 'spacing12' }}>
            <Box
              data-testid="employer-calendar-events_legend-plan-dates"
              background={theme.colors.fund3}
              width="20px"
              height="20px"
              round="snackbarRound"
              margin={{ right: 'spacing4' }}
            />
            <Text size="small" weight={500}>
              Plan Dates
            </Text>
          </Box>

          <Box direction="row" align="center" margin={{ right: 'spacing12' }}>
            <Box
              data-testid="employer-calendar-events_legend-funding"
              background={theme.colors.fund2}
              width="20px"
              height="20px"
              round="snackbarRound"
              margin={{ right: 'spacing4' }}
            />
            <Text size="small" weight={500}>
              Funding
            </Text>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EmployerCalendarEvents;
